/* Dữ liệu Telesales của khách tiềm năng lưu tạm trên máy (localStorage) — chờ nối API */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';
import {
  EMPTY_LOCAL, OUTCOMES, uid, type ApptSt, type ChatMsg, type LeadAppt, type LeadLocal, type Outcome, type Touch,
} from './logic';
import type { Role } from './roles';
import { BRANCHES } from '../dashboard/data';
import { isDemoId, type DemoCustomer } from './demo';
import { DEFAULT_CHAT_REASSIGN, DEFAULT_REASSIGN, type ChatReassignCfg, type ReassignCfg } from './reassign';
import { depositAction, type DepositAction } from './deposit';

export interface ReassignLog {
  id: string;
  at: number;
  customerId: number;
  customer: string;
  from: string | null;
  to: string;
  /** Thời gian không tương tác lúc chia (ms) */
  idle: number;
  mode: 'auto' | 'manual';
  by: string;
  /** call: chia Telesales · chat: chia Trực page */
  kind?: 'call' | 'chat';
}
export interface ReassignItem { id: number; customer: string; from: { id: number; name: string } | null; to: { id: number; name: string }; idle: number }

interface LeadStore {
  view?: Role;
  byId: Record<number, LeadLocal>;
  /** Khách mẫu chỉ có trên máy này */
  demo?: { at: number; customers: DemoCustomer[] };
  seedDemo: (customers: DemoCustomer[], locals: Record<number, LeadLocal>) => void;
  clearDemo: () => void;
  setView: (v: Role) => void;
  patch: (id: number, p: Partial<LeadLocal>) => void;
  addTouch: (id: number, t: Omit<Touch, 'id' | 'at'> & { at?: number }) => void;
  addAppt: (id: number, a: Omit<LeadAppt, 'id' | 'createdAt' | 'status'>) => LeadAppt;
  setApptStatus: (id: number, apptId: string, status: ApptSt, by: string, extra?: Partial<LeadAppt>) => void;
  setOutcome: (id: number, apptId: string, o: Outcome) => void;
  /** Sửa lịch không ghi lịch sử chạm (VD gắn mã lịch trên hệ thống) */
  patchAppt: (id: number, apptId: string, p: Partial<LeadAppt>) => void;
  /** Dời lịch: lịch cũ thành huỷ (trỏ sang lịch mới), lịch mới chờ xác nhận lại */
  moveAppt: (id: number, apptId: string, a: Omit<LeadAppt, 'id' | 'createdAt' | 'status'>, reason?: string) => LeadAppt | undefined;
  assign: (ids: number[], staff: { id: number; name: string }, by: string) => void;
  reassignCfg: ReassignCfg;
  setReassignCfg: (c: ReassignCfg) => void;
  reassignLog: ReassignLog[];
  /** Con trỏ xoay vòng khi chia đều */
  rr: number;
  /** Chia lại hàng loạt; ghi lịch sử chạm cho từng khách và nhật ký chia lại */
  runReassign: (items: ReassignItem[], mode: 'auto' | 'manual', by: string, rule: string, rr: number) => void;
  chatCfg: ChatReassignCfg;
  setChatCfg: (c: ChatReassignCfg) => void;
  rrChat: number;
  /** Chia lại người phụ trách chat (Trực page) */
  runChatReassign: (items: ReassignItem[], mode: 'auto' | 'manual', by: string, rule: string, rr: number) => void;
  addMsg: (id: number, m: Omit<ChatMsg, 'id' | 'at'>) => void;
  /** Thao tác trên khoản cọc (đối soát, đẩy qua POS, hoàn / bỏ cọc); trả về lỗi nếu không làm được */
  depositAct: (leadId: number, quoteId: string, act: DepositAction, by: string) => string | undefined;
}

/** Giữ tối đa N dòng lịch sử chia lại trên máy */
const MAX_LOG = 5000;
const cur = (s: LeadStore, id: number) => s.byId[id] ?? EMPTY_LOCAL;
const APPT_LOG: Record<ApptSt, string> = {
  booked: 'Đặt lại lịch',
  confirmed: 'Khách xác nhận lịch',
  cancelled: 'Huỷ lịch hẹn',
  arrived: 'Khách đến cơ sở · check-in',
  noshow: 'Khách không đến lịch',
  done: 'Hoàn tất tư vấn',
};
const touch = (t: Omit<Touch, 'id' | 'at'>): Touch => ({ ...t, id: uid(), at: Date.now() });

export const useLeadStore = create<LeadStore>()(
  persist(
    (set, get) => ({
      byId: {},
      setView: (view) => set({ view }),
      seedDemo: (customers, locals) => set((s) => ({
        demo: { at: Date.now(), customers },
        byId: { ...Object.fromEntries(Object.entries(s.byId).filter(([k]) => !isDemoId(Number(k)))), ...locals },
      })),
      clearDemo: () => set((s) => ({
        demo: undefined,
        byId: Object.fromEntries(Object.entries(s.byId).filter(([k]) => !isDemoId(Number(k)))),
      })),
      patch: (id, p) => set((s) => ({ byId: { ...s.byId, [id]: { ...cur(s, id), ...p } } })),
      addTouch: (id, t) => set((s) => {
        const l = cur(s, id);
        return { byId: { ...s.byId, [id]: { ...l, touches: [{ ...t, id: uid(), at: t.at ?? Date.now() }, ...l.touches] } } };
      }),
      addAppt: (id, a) => {
        const appt: LeadAppt = { ...a, id: uid(), createdAt: Date.now(), status: 'booked' };
        set((s) => {
          const l = cur(s, id);
          return { byId: { ...s.byId, [id]: { ...l, appts: [appt, ...l.appts] } } };
        });
        return appt;
      },
      setApptStatus: (id, apptId, status, by, extra) => set((s) => {
        const l = cur(s, id);
        const appt = l.appts.find((a) => a.id === apptId);
        if (!appt) return s;
        const text = `${APPT_LOG[status]} · ${dayjs(appt.at).format('HH:mm DD/MM')}${extra?.consultant ? ` · tư vấn: ${extra.consultant}` : ''}${extra?.cancelReason ? ` · lý do: ${extra.cancelReason}` : ''}`;
        return {
          byId: {
            ...s.byId,
            [id]: {
              ...l,
              appts: l.appts.map((a) => (a.id === apptId ? { ...a, ...extra, status } : a)),
              touches: [touch({ kind: 'appt', text, by }), ...l.touches],
            },
          },
        };
      }),
      patchAppt: (id, apptId, p) => set((s) => {
        const l = cur(s, id);
        return { byId: { ...s.byId, [id]: { ...l, appts: l.appts.map((a) => (a.id === apptId ? { ...a, ...p } : a)) } } };
      }),
      moveAppt: (id, apptId, a, reason) => {
        const old = cur(get(), id).appts.find((x) => x.id === apptId);
        if (!old) return undefined;
        const appt: LeadAppt = { ...a, id: uid(), createdAt: Date.now(), status: 'booked', movedFrom: apptId };
        const text = `Dời lịch ${dayjs(old.at).format('HH:mm DD/MM')} → ${dayjs(appt.at).format('HH:mm DD/MM')} · ${appt.branch}${reason ? ` · lý do: ${reason}` : ''}`;
        set((s) => {
          const l = cur(s, id);
          return {
            byId: {
              ...s.byId,
              [id]: {
                ...l,
                appts: [appt, ...l.appts.map((x) => (x.id === apptId ? { ...x, status: 'cancelled' as const, movedTo: appt.id, cancelReason: reason } : x))],
                touches: [touch({ kind: 'appt', text, by: a.by }), ...l.touches],
              },
            },
          };
        });
        return appt;
      },
      setOutcome: (id, apptId, o) => set((s) => {
        const l = cur(s, id);
        const money = o.amount ? ` · ${o.amount.toLocaleString('vi-VN')} đ` : '';
        return {
          byId: {
            ...s.byId,
            [id]: {
              ...l,
              appts: l.appts.map((a) => (a.id === apptId ? { ...a, status: 'done', outcome: o } : a)),
              touches: [touch({ kind: 'appt', text: `Kết quả tư vấn: ${OUTCOMES[o.kind].label}${money}${o.note ? ` · “${o.note}”` : ''}`, by: o.by }), ...l.touches],
            },
          },
        };
      }),
      assign: (ids, staff, by) => set((s) => {
        const byId = { ...s.byId };
        ids.forEach((id) => {
          const l = cur(s, id);
          byId[id] = { ...l, assign: staff, assignAt: Date.now(), touches: [touch({ kind: 'stage', text: `Phân công Telesales → ${staff.name}`, by }), ...l.touches] };
        });
        return { byId };
      }),
      reassignCfg: DEFAULT_REASSIGN,
      setReassignCfg: (reassignCfg) => set({ reassignCfg }),
      reassignLog: [],
      rr: 0,
      runReassign: (items, mode, by, rule, rr) => set((s) => {
        const at = Date.now();
        const byId = { ...s.byId };
        const logs: ReassignLog[] = items.map((it) => {
          const l = cur(s, it.id);
          const text = `${mode === 'auto' ? 'Tự động chia lại' : 'Chia lại'} Telesales: ${it.from?.name ?? 'chưa phân'} → ${it.to.name} · ${rule}`;
          byId[it.id] = { ...l, assign: it.to, assignAt: at, reassignCount: (l.reassignCount ?? 0) + 1, touches: [touch({ kind: 'stage', text, by }), ...l.touches] };
          return { id: uid(), at, customerId: it.id, customer: it.customer, from: it.from?.name ?? null, to: it.to.name, idle: it.idle, mode, by };
        });
        return { byId, rr, reassignLog: [...logs, ...s.reassignLog].slice(0, MAX_LOG) };
      }),
      chatCfg: DEFAULT_CHAT_REASSIGN,
      setChatCfg: (chatCfg) => set({ chatCfg }),
      rrChat: 0,
      runChatReassign: (items, mode, by, rule, rr) => set((s) => {
        const at = Date.now();
        const byId = { ...s.byId };
        const logs: ReassignLog[] = items.map((it) => {
          const l = cur(s, it.id);
          const text = `${mode === 'auto' ? 'Tự động chia lại' : 'Chia lại'} Trực page phụ trách chat: ${it.from?.name ?? 'chưa phân'} → ${it.to.name} · ${rule}`;
          byId[it.id] = { ...l, chatAssign: it.to, chatAssignAt: at, chatReassignCount: (l.chatReassignCount ?? 0) + 1, touches: [touch({ kind: 'stage', text, by }), ...l.touches] };
          return { id: uid(), at, customerId: it.id, customer: it.customer, from: it.from?.name ?? null, to: it.to.name, idle: it.idle, mode, by, kind: 'chat' };
        });
        return { byId, rrChat: rr, reassignLog: [...logs, ...s.reassignLog].slice(0, MAX_LOG) };
      }),
      addMsg: (id, m) => set((s) => {
        const l = cur(s, id);
        return { byId: { ...s.byId, [id]: { ...l, msgs: [...l.msgs, { ...m, id: uid(), at: Date.now() }] } } };
      }),
      depositAct: (leadId, quoteId, act, by) => {
        const { byId, error } = depositAction(get().byId, leadId, quoteId, act, by);
        if (!error) set({ byId });
        return error;
      },
    }),
    {
      name: 'khothi-tele-leads-v1',
      version: 6,
      // v2: cài đặt chia lại khách · v3: chia lại chat · v5: bỏ sổ ví thử nghiệm ở v4 (cọc đẩy qua POS thành đơn hàng)
      // v6: chỉ còn một cơ sở — lịch hẹn ở cơ sở khác chuyển về cơ sở đầu danh sách
      migrate: (state, from) => {
        const { wallet: _wallet, ...rest } = state as Record<string, unknown>;
        const byId = rest.byId as Record<number, LeadLocal> | undefined;
        const home = BRANCHES[0].name;
        const one = (l: LeadLocal): LeadLocal => ({ ...l, appts: l.appts.map((a) => (BRANCHES.some((b) => b.name === a.branch) ? a : { ...a, branch: home })) });
        return {
          ...rest,
          ...(from < 6 && byId ? { byId: Object.fromEntries(Object.entries(byId).map(([k, l]) => [k, one(l)])) } : {}),
          ...(from < 2 ? { reassignCfg: DEFAULT_REASSIGN, reassignLog: [], rr: 0 } : {}),
          ...(from < 3 ? { chatCfg: DEFAULT_CHAT_REASSIGN, rrChat: 0 } : {}),
        } as unknown as LeadStore;
      },
    },
  ),
);

// Mở nhiều tab trên cùng trình duyệt (Kế toán, POS, Tư vấn): tab khác đổi dữ liệu thì đọc lại, không chờ tải lại trang
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === 'khothi-tele-leads-v1') useLeadStore.persist.rehydrate(); });
}

/** Tên người đang đăng nhập để ghi vào lịch sử chạm */
export const currentStaffName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.info?.fullname || u?.info?.name || u?.fullname || u?.name || 'Tôi';
  } catch {
    return 'Tôi';
  }
};
